'use client';
import { FC, useEffect, useMemo, useState } from 'react';
import MapComponent from '../map.component';

import { AddressSrc, ICoordinate, ILocation, IRegion_SELL } from '@/types';
import { findPointInPolygon, ILocationDropDown, ISmallArea, useTranslation, useWindowDimensions } from '@/hook';
import { NEXT_APP_SYLNDR_GOOGLE_MAP_API_KEY } from '@/constants';
import { Libraries, useLoadScript } from '@react-google-maps/api';
import  SwipeableDrawer from '@/components/shared/bottom-sheet/bottom-sheet.component';
import Modal from '@/components/shared/modal.component';
import Typography from '@/components/shared/typography/typography.component';
import AutocompleteComponent from '../auto-complete/auto-complete.component';
import { LocationIconGreen } from '../../../../../public/Images';
import styles from '../styles.module.scss';
import Image from 'next/image';
// const libraries: MapLibraries = ['places', 'geometry'];
const libraries: Libraries = ['places', 'geometry'];

interface IMapComponentProps {
    regions: ILocationDropDown[];
    region: IRegion_SELL;
    buttonClicked: (
        id: string,
        value: string,
        address: string,
        city: string,
        coordinate: ICoordinate
    ) => void;
}

const MapWrapper: FC<IMapComponentProps> = ({
    regions,
    buttonClicked,
    region,
}) => {
    const defaultLoc: ILocation = {
        city: region?.addressCity,
        address: region?.address,
        coordinate: region?.coordinate || { lat: 30.0444, lng: 31.2357 },
    };

    const [location, setLocation] = useState<ILocation>(defaultLoc);
    const [locationOpen, setLocationOpen] = useState<boolean>(false);
    const [addressSrc, setAddressSrc] =
        useState<AddressSrc>('reverseGeolocation');
    const { t, IS_RTL } = useTranslation();
    const { IS_DESKTOP } = useWindowDimensions();

    const formatPolygons = useMemo<ISmallArea[]>(() => {
        const areas: ISmallArea[] = [];
        regions.forEach((city: ILocationDropDown) => {
            areas.push(...city.smallArea);
        });
        return areas;
    }, [region, regions]);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: NEXT_APP_SYLNDR_GOOGLE_MAP_API_KEY,
        libraries: libraries,
    });

    const onPlaceChanged = (result: google.maps.places.PlaceResult) => {
        setAddressSrc('autocomplete');
        if (
            result &&
            result?.geometry?.location &&
            result?.formatted_address &&
            result?.address_components
        ) {
            const { lat, lng } = result.geometry.location;
            setLocation({
                address: result.formatted_address,
                coordinate: { lat: lat(), lng: lng() },
                city: result.address_components?.filter(
                    item =>
                        item?.types?.includes('locality') ||
                        item?.types?.includes('administrative_area_level_2') ||
                        item?.types?.includes('administrative_area_level_1')
                )[0]?.short_name,
            });
            setLocationOpen(true);
        }
    };

    const handleSetLocation = (loc: ILocation) => {
        setLocation(loc);
        const currArea = findPointInPolygon(
            isLoaded,
            formatPolygons,
            loc?.coordinate
        );
        setLocationOpen(false);
        buttonClicked(
            currArea?.id || '',
            currArea?.nameEn || '',
            loc.address,
            currArea ? (IS_RTL ? currArea.nameAr : currArea.nameEn) : loc.city,
            loc.coordinate
        );
    };

    const setLocationOpenHandle = (open: boolean) => {
        if (!open && IS_DESKTOP) {
            setLocation(defaultLoc);
        }
        setLocationOpen(open);
    };
    useEffect(() => {
        setLocation({
            city: defaultLoc.city,
            address: defaultLoc.address,
            coordinate: defaultLoc.coordinate,
        });
    }, [region]);
    if (!isLoaded) return null;

    return (
        <>
            {IS_DESKTOP ? (
                <Modal
                    sx={{ zIndex: 5500000 }}
                    open={locationOpen}
                    setOpen={setLocationOpenHandle}
                >
                    <MapComponent
                        setAddressSrc={setAddressSrc}
                        addressSrc={addressSrc}
                        setLocation={handleSetLocation}
                        location={location}
                        setLocationOpen={setLocationOpenHandle}
                    />
                </Modal>
            ) : (
                <SwipeableDrawer
                    open={locationOpen}
                    setOpen={setLocationOpenHandle}
                >
                    {locationOpen ? (
                        <MapComponent
                            setAddressSrc={setAddressSrc}
                            addressSrc={addressSrc}
                            setLocation={handleSetLocation}
                            location={location}
                            setLocationOpen={setLocationOpenHandle}
                        />
                    ) : (
                        <></>
                    )}
                </SwipeableDrawer>
            )}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    flex: 1,
                }}
            >
                <Typography variant="body2Medium" style={{ display: 'inline' }}>
                    {t('address')}
                </Typography>
                <div className={styles.input_group_container} id="autocomplete">
                    <AutocompleteComponent
                        onPlaceChanged={onPlaceChanged}
                        value={location.address}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        gap: '8px',
                        marginInlineEnd: 'auto',
                        marginBlock: '2px',
                    }}
                    onClick={() => setLocationOpenHandle(true)}
                >
                    <Image
                        src={LocationIconGreen.src}
                        width={LocationIconGreen.width}
                        height={LocationIconGreen.height}
                        alt="location"
                    />
                    <Typography
                        color={'#00673E'}
                        style={{
                            textDecoration: 'underline',
                            cursor: 'default',
                        }}
                    >
                        {t('selectLocationOnMap')}
                    </Typography>
                </div>
            </div>
        </>
    );
};

export default MapWrapper;
